/**
 * `DeletionsView` — the ADR-0180 slice-3c-2 account-deletion approval
 * board. When a merchant's 30-day deletion grace expires, the grace
 * cron proposes an `account_deletion_run` here (manual mode); approving
 * runs the IRREVERSIBLE anonymize-and-retain hard delete (typed-confirm),
 * rejecting leaves the account soft-deleted with a reason. Governed by
 * the same manual/auto toggle as purges (on the Purge page).
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminDeletionRun, AdminInGraceDeletion } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { TypedConfirmDialog } from '../../components/atoms/TypedConfirmDialog.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { JobLastRunNote } from '../jobs/JobLastRunNote.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

function graceDaysLeft(grace_until: string | null): number | null {
  if (grace_until === null) return null;
  const ms = new Date(grace_until).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function statusTone(s: AdminDeletionRun['status']): PillTone {
  switch (s) {
    case 'pending': return 'amber';
    case 'approved': return 'sky';
    case 'executed': return 'lime';
    case 'rejected': return 'neutral';
    case 'failed': return 'coral';
  }
}

export function DeletionsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [runs, setRuns] = useState<readonly AdminDeletionRun[]>([]);
  const [inGrace, setInGrace] = useState<readonly AdminInGraceDeletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  // Auto vs manual deletion mode (`account_deletion_auto`). null until loaded.
  const [autoMode, setAutoMode] = useState<boolean | null>(null);
  const [togglePending, setTogglePending] = useState(false);
  const [autoConfirmOpen, setAutoConfirmOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [res, settingsRes] = await Promise.all([
        api.listDeletionRuns(),
        api.listSettings(),
      ]);
      setRuns(res.runs);
      setInGrace(res.in_grace ?? []);
      setAutoMode(
        settingsRes.settings.find((s) => s.key === 'account_deletion_auto')?.bool_value ?? false,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const applyToggle = async (next: boolean): Promise<void> => {
    setTogglePending(true);
    try {
      const res = await api.setSetting('account_deletion_auto', next);
      setAutoMode(
        res.settings.find((s) => s.key === 'account_deletion_auto')?.bool_value ?? next,
      );
      setAutoConfirmOpen(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.kind === 'forbidden'
          ? 'Superadmin required to change the mode.'
          : 'Failed to change mode — retry.',
      );
    } finally {
      setTogglePending(false);
    }
  };

  // Switching TO automatic arms unattended deletion — confirm it.
  // Switching back to manual is safe + immediate.
  const toggleAuto = (): void => {
    if (autoMode === null || togglePending) return;
    if (!autoMode) setAutoConfirmOpen(true);
    else void applyToggle(false);
  };

  const approve = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await api.approveDeletionRun(id);
      setConfirmId(null);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.kind === 'forbidden'
          ? 'Superadmin required.'
          : err instanceof ApiError && err.kind === 'conflict'
            ? 'Already decided — refresh.'
            : 'Approve failed — check logs.',
      );
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id: string): Promise<void> => {
    const reason = (rejectReason[id] ?? '').trim();
    if (reason.length === 0) return;
    setBusy(true);
    try {
      await api.rejectDeletionRun(id, reason);
      await load();
    } catch {
      setError('Reject failed — retry.');
    } finally {
      setBusy(false);
    }
  };

  const pending = runs.filter((r) => r.status === 'pending');
  const history = runs.filter((r) => r.status !== 'pending');

  return (
    <div style={{ padding: 24, maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>
            ADR-0180 · Account deletion
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Deletion approvals</h1>
        </div>
        <button type="button" onClick={() => void load()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Deletion mode — auto vs manual (account_deletion_auto). Separate
          from the purge toggle by design (ADR-0180 security review). */}
      {autoMode !== null ? (
        <Card style={{ padding: 16, marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>
              Deletion mode: {autoMode ? 'Automatic' : 'Manual (approval required)'}
            </div>
            <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, maxWidth: '62ch', lineHeight: 1.5 }}>
              {autoMode
                ? 'At each account’s grace-window expiry the irreversible delete runs automatically — no approval here. The 30-day cancellable grace and per-night cap still apply.'
                : 'Grace-expired accounts wait here for a superadmin to approve each irreversible delete.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => toggleAuto()}
            disabled={togglePending}
            style={{
              fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, padding: '8px 14px',
              background: autoMode ? 'transparent' : C.coralFg,
              color: autoMode ? C.body : C.ink,
              border: autoMode ? `1px solid ${C.border}` : 'none',
              borderRadius: 3, cursor: 'pointer', opacity: togglePending ? 0.6 : 1, whiteSpace: 'nowrap',
            }}
          >
            {autoMode ? 'Switch to manual' : 'Switch to automatic'}
          </button>
        </Card>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}

      {loading ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {/* In-grace deletions — requested but still cancellable; shown
          immediately (not only at grace expiry) with the request
          fingerprint (founder request 2026-07-13). Read-only. */}
      {!loading && inGrace.length > 0 ? (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>
            In grace · requested, awaiting expiry ({inGrace.length})
          </div>
          {inGrace.map((g) => {
            const left = graceDaysLeft(g.grace_until);
            return (
              <Card key={g.account_id} style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                  <Pill tone="sky">in grace</Pill>
                  <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {g.owner_email ?? g.requested_by_name ?? 'Unknown owner'}
                  </span>
                  {g.site_label !== null ? (
                    <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{g.site_label}</span>
                  ) : null}
                  <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                    {left ?? 0} day{left === 1 ? '' : 's'} left · deletes {g.grace_until !== null ? formatIsoCompact(g.grace_until) : '—'}
                  </span>
                </div>
                <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
                  requested {formatIsoCompact(g.requested_at)} by {g.requested_by_name ?? (g.requested_by === null ? 'system' : 'the owner')}
                  {g.requested_ip !== null ? ` · ${g.requested_ip}` : ''}
                  {g.requested_geo !== null ? ` · ${g.requested_geo}` : ''}
                </div>
                <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted, marginTop: 6 }}>
                  Cancellable by the owner until expiry. It becomes approvable here when the grace window ends.
                </div>
              </Card>
            );
          })}
        </div>
      ) : null}

      {!loading && pending.length === 0 && error === null ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>
            Nothing pending. When a merchant’s 30-day deletion grace elapses, an
            approval appears here (in manual mode). Approving runs the
            irreversible anonymize-and-retain delete; the signed audit chain is
            retained, all personal data erased.
          </div>
          <div style={{ marginTop: 8 }}>
            <JobLastRunNote job_name="job:account_deletion_grace" refreshKey={pending} />
          </div>
        </Card>
      ) : null}

      {pending.map((r) => (
        <Card key={r.deletion_run_id} style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <Pill tone="amber">pending</Pill>
            <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text, minWidth: 0, overflowWrap: 'anywhere' }}>
              {r.owner_email ?? r.requested_by_name ?? 'Owner scrubbed'}
            </span>
            {r.site_label !== null ? (
              <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>{r.site_label}</span>
            ) : null}
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
              grace elapsed {r.grace_until !== null ? formatIsoCompact(r.grace_until) : '—'}
            </span>
          </div>
          {/* Forensic fingerprint of the original request (ADR-0180 2026-07-13). */}
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.7 }}>
            requested by {r.requested_by_name ?? (r.requested_by === null ? 'system' : 'the owner')}
            {r.requested_ip !== null ? ` · ${r.requested_ip}` : ''}
            {r.requested_geo !== null ? ` · ${r.requested_geo}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setConfirmId(r.deletion_run_id)} disabled={busy}
              style={{ fontFamily: sansFont, fontSize: 12.5, fontWeight: 600, padding: '8px 14px', background: C.coralFg, color: C.ink, border: 'none', borderRadius: 3, cursor: 'pointer' }}>
              Approve &amp; delete
            </button>
            <input type="text" value={rejectReason[r.deletion_run_id] ?? ''}
              onChange={(e) => setRejectReason((p) => ({ ...p, [r.deletion_run_id]: e.target.value }))}
              placeholder="Reject reason…"
              style={{ fontFamily: sansFont, fontSize: 12.5, padding: '8px 10px', background: C.surface2 ?? 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, minWidth: 200 }} />
            <button type="button" onClick={() => void reject(r.deletion_run_id)}
              disabled={busy || (rejectReason[r.deletion_run_id] ?? '').trim().length === 0}
              style={{ fontFamily: sansFont, fontSize: 12.5, padding: '8px 14px', background: 'transparent', color: C.body, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}>
              Reject
            </button>
          </div>
        </Card>
      ))}

      {history.length > 0 ? (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>History</div>
          {history.map((r) => (
            <div key={r.deletion_run_id}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 4px', borderBottom: `1px solid ${C.border}`, fontFamily: sansFont, fontSize: 12.5, color: C.body, flexWrap: 'wrap' }}>
              <Pill tone={statusTone(r.status)}>{r.status}</Pill>
              {/* Human label if we still have one; a hard-deleted account is
                  scrubbed, so its account id is the only remaining identifier. */}
              <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
                {r.owner_email ?? r.requested_by_name ?? r.account_id}
              </span>
              {r.site_label !== null ? <span style={{ color: C.muted }}>{r.site_label}</span> : null}
              <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>{formatIsoCompact(r.created_at)}</span>
              {r.reject_reason !== null ? <span style={{ color: C.muted }}>“{r.reject_reason}”</span> : null}
            </div>
          ))}
        </div>
      ) : null}

      <TypedConfirmDialog
        open={confirmId !== null}
        title="Approve account deletion"
        body={
          <span>
            This permanently erases the account’s personal data (users, sites,
            connectors, buyer details) and is <strong>irreversible</strong>. The
            signed audit chain is retained in anonymized form.
          </span>
        }
        expected="delete"
        inputLabel={'Type "delete" to confirm'}
        confirmLabel="Approve & execute"
        pending={busy}
        onConfirm={() => { if (confirmId !== null) void approve(confirmId); }}
        onCancel={() => setConfirmId(null)}
      />

      <TypedConfirmDialog
        open={autoConfirmOpen}
        title="Switch deletion to automatic"
        body={
          <span>
            Grace-expired accounts will be <strong>permanently deleted without
            approval</strong> from this board. The 30-day cancellable grace and
            the per-night cap still apply, but the manual review step is removed.
            You can switch back to manual at any time.
          </span>
        }
        expected="automatic"
        inputLabel={'Type "automatic" to confirm'}
        confirmLabel="Enable automatic deletion"
        pending={togglePending}
        onConfirm={() => { void applyToggle(true); }}
        onCancel={() => setAutoConfirmOpen(false)}
      />
    </div>
  );
}
